import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../../common/container';

const BusinessMessagingHub = () => (
  <svg viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl mx-auto">
    {/* Background Elements */}
    <defs>
      <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.05" />
      </linearGradient>
      <linearGradient id="whatsappGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#25D366" />
        <stop offset="100%" stopColor="#128C7E" />
      </linearGradient>
      <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>
    
    {/* Subtle Background Shape */}
    <path d="M100,350 Q400,250 700,350" stroke="#F3F4F6" strokeWidth="80" fill="none" />
    
    {/* Central Business Hub */}
    <g transform="translate(400, 180)">
      {/* Pulsing Circle */}
      <circle cx="0" cy="0" r="80" fill="url(#purpleGradient)">
        <animate 
          attributeName="opacity" 
          values="1;0.7;1" 
          dur="4s" 
          repeatCount="indefinite" 
        />
      </circle>
      
      {/* Main Business Hub */}
      <circle cx="0" cy="0" r="70" fill="#FFFFFF" stroke="#8B5CF6" strokeWidth="2" />
      
      {/* Business Icon - Storefront/Shop icon */}
      <g transform="translate(0, 0)">
        <path d="M-25,-15 L25,-15 L25,15 L-25,15 Z" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M-25,-15 L-20,-25 L20,-25 L25,-15" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M-15,-15 L-15,15" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M5,-15 L5,15" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M-5,0 L15,0" fill="none" stroke="#8B5CF6" strokeWidth="2" />
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="90" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="14" fontWeight="bold">SME</text>
    </g>
    
    {/* WhatsApp Conversation Flow - Left Side */}
    <g transform="translate(200, 180)">
      {/* WhatsApp Platform */}
      <circle cx="0" cy="0" r="60" fill="white" stroke="#ddd" strokeWidth="2" />
      
      {/* WhatsApp Logo */}
      <circle cx="0" cy="0" r="40" fill="url(#whatsappGradient)" />
      <path d="M-18,10 L-8,20 L20,-8" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      
      {/* Flow Connection */}
      <path d="M50,0 L130,0" stroke="#8B5CF6" strokeWidth="3" fill="none" strokeDasharray="4,4">
        <animate 
          attributeName="stroke-dashoffset" 
          values="0;16" 
          dur="1s" 
          repeatCount="indefinite" 
        />
      </path>
      
      {/* Static dots instead of animated circles */}
      <circle cx="70" cy="-15" r="8" fill="#8B5CF6" opacity="0.7" />
      <circle cx="90" cy="15" r="6" fill="#8B5CF6" opacity="0.9" />
      <circle cx="110" cy="-10" r="5" fill="#8B5CF6" opacity="0.8" />
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="95" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="14" fontWeight="bold">WhatsApp</text>
    </g>
    
    {/* Business Value Flow - Right Side */}
    <g transform="translate(600, 180)">
      {/* Business Outcomes */}
      <circle cx="0" cy="0" r="60" fill="white" stroke="#ddd" strokeWidth="2" />
      
      {/* Growth Chart Icon */}
      <g transform="translate(0, 0)">
        <rect x="-30" y="-30" width="60" height="60" rx="4" fill="#F9FAFB" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M-25,10 L-10,0 L5,15 L25,-20" stroke="#8B5CF6" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="-25" cy="10" r="4" fill="#8B5CF6" />
        <circle cx="-10" cy="0" r="4" fill="#8B5CF6" />
        <circle cx="5" cy="15" r="4" fill="#8B5CF6" />
        <circle cx="25" cy="-20" r="4" fill="#8B5CF6" />
      </g>
      
      {/* Flow Connection */}
      <path d="M-50,0 L-130,0" stroke="#8B5CF6" strokeWidth="3" fill="none" strokeDasharray="4,4">
        <animate 
          attributeName="stroke-dashoffset" 
          values="0;16" 
          dur="1s" 
          repeatCount="indefinite" 
        />
      </path>
      
      {/* Static symbols instead of animated ones */}
      <path d="M-70,-15 L-60,-5 L-70,5 L-80,-5 Z" fill="#8B5CF6" opacity="0.7" />
      <rect x="-100" y="10" width="10" height="10" fill="#8B5CF6" opacity="0.9" />
      <circle cx="-85" cy="0" r="5" fill="#8B5CF6" opacity="0.8" />
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="95" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="14" fontWeight="bold">Business Growth</text>
    </g>
    
    {/* Platform Capabilities Nodes */}
    <g>
      {/* Sales Node */}
      <g transform="translate(340, 70)">
        <circle cx="0" cy="0" r="35" fill="white" stroke="#8B5CF6" strokeWidth="2" />
        
        {/* Sales Icon - dollar sign/shopping cart */}
        <g transform="translate(0, 0)">
          <circle cx="0" cy="0" r="17" fill="none" stroke="#8B5CF6" strokeWidth="2" />
          <path d="M0,-10 L0,10" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
          <path d="M5,0 L-5,0" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
          <path d="M-5,-5 L-5,5" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
        </g>
        
        {/* Connection line */}
        <path d="M0,30 L0,60 C0,60 60,60 60,110" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="4,4" fill="none" />
        
        {/* Text label OUTSIDE the node */}
        <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12">Sales</text>
      </g>
      
      {/* Support Node */}
      <g transform="translate(460, 70)">
        <circle cx="0" cy="0" r="35" fill="white" stroke="#8B5CF6" strokeWidth="2" />
        
        {/* Support Icon - headset/customer service */}
        <g transform="translate(0, 0)">
          <path d="M-12,0 A12,12 0 0,1 12,0" stroke="#8B5CF6" strokeWidth="2" fill="none" />
          <path d="M-12,0 L-12,10 C-12,15 -8,15 -8,10 L-8,0" stroke="#8B5CF6" strokeWidth="2" fill="none" />
          <path d="M12,0 L12,10 C12,15 8,15 8,10 L8,0" stroke="#8B5CF6" strokeWidth="2" fill="none" />
          <rect x="-5" y="10" width="10" height="5" fill="#8B5CF6" />
        </g>
        
        {/* Connection line */}
        <path d="M0,30 L0,60 C0,60 -60,60 -60,110" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="4,4" fill="none" />
        
        {/* Text label OUTSIDE the node */}
        <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12">Support</text>
      </g>
      
      {/* Payments Node */}
      <g transform="translate(340, 290)">
        <circle cx="0" cy="0" r="35" fill="white" stroke="#8B5CF6" strokeWidth="2" />
        
        {/* Payments Icon - credit card/money */}
        <g transform="translate(0, 0)">
          <rect x="-15" y="-10" width="30" height="20" rx="2" fill="none" stroke="#8B5CF6" strokeWidth="2" />
          <path d="M-15,0 L15,0" stroke="#8B5CF6" strokeWidth="2" />
          <path d="M-10,-5 L0,-5" stroke="#8B5CF6" strokeWidth="2" />
          <circle cx="10" cy="5" r="3" fill="#8B5CF6" />
        </g>
        
        {/* Connection line */}
        <path d="M0,-30 L0,-60 C0,-60 60,-60 60,-110" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="4,4" fill="none" />
        
        {/* Text label OUTSIDE the node */}
        <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12">Payments</text>
      </g>
      
      {/* Marketing Node */}
      <g transform="translate(460, 290)">
        <circle cx="0" cy="0" r="35" fill="white" stroke="#8B5CF6" strokeWidth="2" />
        
        {/* Marketing Icon - megaphone/broadcast */}
        <g transform="translate(0, 0)">
          <path d="M-15,0 L0,-10 L0,10 Z" fill="#8B5CF6" />
          <path d="M0,0 L15,0" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
          <path d="M10,-5 L15,-10" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
          <path d="M10,5 L15,10" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
        </g>
        
        {/* Connection line */}
        <path d="M0,-30 L0,-60 C0,-60 -60,-60 -60,-110" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="4,4" fill="none" />
        
        {/* Text label OUTSIDE the node */}
        <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12">Marketing</text>
      </g>
    </g>
    
    {/* Labels for the main flows */}
    <text x="325" y="160" textAnchor="middle" fill="#666" fontFamily="system-ui" fontSize="10">Customer Interactions</text>
    <text x="475" y="160" textAnchor="middle" fill="#666" fontFamily="system-ui" fontSize="10">Business Intelligence</text>
  </svg>
);

const DeadstockRedistributionHub = () => (
  <svg viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl mx-auto">
    {/* Background Elements */}
    <defs>
      <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.05" />
      </linearGradient>
      <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#EF4444" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#EF4444" stopOpacity="0.05" />
      </linearGradient>
      <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#10B981" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#10B981" stopOpacity="0.05" />
      </linearGradient>
    </defs>
    
    {/* Subtle Background */}
    <rect x="0" y="0" width="800" height="400" fill="#F9FAFB" rx="20" />
    <path d="M100,350 Q400,250 700,350" stroke="#F3F4F6" strokeWidth="80" fill="none" />
    
    {/* Central Platform */}
    <g transform="translate(400, 200)">
      <circle cx="0" cy="0" r="70" fill="#FFFFFF" stroke="#8B5CF6" strokeWidth="2" />
      
      {/* Platform Icon - Network/Hub */}
      <g transform="translate(0, 0)">
        <circle cx="0" cy="0" r="25" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <circle cx="-20" cy="-20" r="10" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <circle cx="20" cy="-20" r="10" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <circle cx="-20" cy="20" r="10" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <circle cx="20" cy="20" r="10" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <line x1="-15" y1="-15" x2="-5" y2="-5" stroke="#8B5CF6" strokeWidth="2" />
        <line x1="15" y1="-15" x2="5" y2="-5" stroke="#8B5CF6" strokeWidth="2" />
        <line x1="-15" y1="15" x2="-5" y2="5" stroke="#8B5CF6" strokeWidth="2" />
        <line x1="15" y1="15" x2="5" y2="5" stroke="#8B5CF6" strokeWidth="2" />
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="95" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="16" fontWeight="bold">Inventory Platform</text>
      
      {/* Connection lines to stores */}
      <g stroke="#8B5CF6" strokeWidth="2" strokeDasharray="5,5">
        <line x1="0" y1="-70" x2="0" y2="-120" />
        <line x1="-60" y1="-35" x2="-120" y2="-70" />
        <line x1="-60" y1="35" x2="-120" y2="70" />
        <line x1="0" y1="70" x2="0" y2="120" />
        <line x1="60" y1="35" x2="120" y2="70" />
        <line x1="60" y1="-35" x2="120" y2="-70" />
      </g>
    </g>
    
    {/* Stores with excess inventory (red) */}
    <g transform="translate(280, 80)">
      <circle cx="0" cy="0" r="40" fill="white" stroke="#EF4444" strokeWidth="2" />
      
      {/* Excess Inventory Icon - Stacked Boxes */}
      <g transform="translate(0, 0)">
        <rect x="-20" y="-15" width="40" height="30" rx="2" fill="none" stroke="#EF4444" strokeWidth="2" />
        <rect x="-15" y="-10" width="30" height="20" fill="#EF4444" opacity="0.1" />
        <rect x="-12" y="-25" width="24" height="10" rx="1" fill="none" stroke="#EF4444" strokeWidth="1.5" />
        <rect x="-8" y="-35" width="16" height="10" rx="1" fill="none" stroke="#EF4444" strokeWidth="1.5" />
        <text x="0" y="5" textAnchor="middle" fill="#EF4444" fontFamily="system-ui" fontSize="14" fontWeight="bold">+</text>
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12" fontWeight="medium">Excess Stock</text>
      
      {/* Inventory flowing to platform */}
      <g>
        <rect x="30" y="30" width="10" height="8" fill="#EF4444" rx="1">
          <animate attributeName="x" values="30;100" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="30;80" dur="2s" repeatCount="indefinite" />
        </rect>
      </g>
    </g>
    
    <g transform="translate(520, 80)">
      <circle cx="0" cy="0" r="40" fill="white" stroke="#EF4444" strokeWidth="2" />
      
      {/* Excess Inventory Icon - Stacked Boxes */}
      <g transform="translate(0, 0)">
        <rect x="-20" y="-15" width="40" height="30" rx="2" fill="none" stroke="#EF4444" strokeWidth="2" />
        <rect x="-15" y="-10" width="30" height="20" fill="#EF4444" opacity="0.1" />
        <rect x="-12" y="-25" width="24" height="10" rx="1" fill="none" stroke="#EF4444" strokeWidth="1.5" />
        <rect x="-8" y="-35" width="16" height="10" rx="1" fill="none" stroke="#EF4444" strokeWidth="1.5" />
        <text x="0" y="5" textAnchor="middle" fill="#EF4444" fontFamily="system-ui" fontSize="14" fontWeight="bold">+</text>
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12" fontWeight="medium">Excess Stock</text>
      
      {/* Inventory flowing to platform */}
      <g>
        <rect x="-30" y="30" width="10" height="8" fill="#EF4444" rx="1">
          <animate attributeName="x" values="-30;-100" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="30;80" dur="2s" repeatCount="indefinite" />
        </rect>
      </g>
    </g>
    
    {/* Stores needing inventory (green) */}
    <g transform="translate(280, 320)">
      <circle cx="0" cy="0" r="40" fill="white" stroke="#10B981" strokeWidth="2" />
      
      {/* Low Inventory Icon - Empty Shelves */}
      <g transform="translate(0, 0)">
        <rect x="-20" y="-15" width="40" height="30" rx="2" fill="none" stroke="#10B981" strokeWidth="2" />
        <line x1="-15" y1="-5" x2="15" y2="-5" stroke="#10B981" strokeWidth="1.5" />
        <line x1="-15" y1="5" x2="15" y2="5" stroke="#10B981" strokeWidth="1.5" />
        <line x1="-15" y1="15" x2="15" y2="15" stroke="#10B981" strokeWidth="1.5" />
        <text x="0" y="-20" textAnchor="middle" fill="#10B981" fontFamily="system-ui" fontSize="10">Low</text>
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12" fontWeight="medium">Needs Stock</text>
      
      {/* Inventory flowing from platform */}
      <g>
        <rect x="30" y="-30" width="10" height="8" fill="#10B981" rx="1">
          <animate attributeName="x" values="100;30" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="-80;-30" dur="2s" repeatCount="indefinite" />
        </rect>
      </g>
    </g>
    
    <g transform="translate(520, 320)">
      <circle cx="0" cy="0" r="40" fill="white" stroke="#10B981" strokeWidth="2" />
      
      {/* Low Inventory Icon - Empty Shelves */}
      <g transform="translate(0, 0)">
        <rect x="-20" y="-15" width="40" height="30" rx="2" fill="none" stroke="#10B981" strokeWidth="2" />
        <line x1="-15" y1="-5" x2="15" y2="-5" stroke="#10B981" strokeWidth="1.5" />
        <line x1="-15" y1="5" x2="15" y2="5" stroke="#10B981" strokeWidth="1.5" />
        <line x1="-15" y1="15" x2="15" y2="15" stroke="#10B981" strokeWidth="1.5" />
        <text x="0" y="-20" textAnchor="middle" fill="#10B981" fontFamily="system-ui" fontSize="10">Low</text>
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12" fontWeight="medium">Needs Stock</text>
      
      {/* Inventory flowing from platform */}
      <g>
        <rect x="-30" y="-30" width="10" height="8" fill="#10B981" rx="1">
          <animate attributeName="x" values="-100;-30" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="-80;-30" dur="2s" repeatCount="indefinite" />
        </rect>
      </g>
    </g>
    
    {/* Side stores */}
    <g transform="translate(200, 200)">
      <circle cx="0" cy="0" r="40" fill="white" stroke="#8B5CF6" strokeWidth="2" />
      
      {/* Store Icon */}
      <g transform="translate(0, 0)">
        <rect x="-15" y="-15" width="30" height="30" rx="2" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M-15,-15 L-10,-25 L10,-25 L15,-15" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <line x1="-8" y1="-5" x2="8" y2="-5" stroke="#8B5CF6" strokeWidth="1.5" />
        <line x1="-8" y1="5" x2="8" y2="5" stroke="#8B5CF6" strokeWidth="1.5" />
        <line x1="-8" y1="15" x2="8" y2="15" stroke="#8B5CF6" strokeWidth="1.5" />
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12" fontWeight="medium">Retailer</text>
      
      {/* Bidirectional arrows */}
      <g>
        <line x1="40" y1="0" x2="80" y2="0" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="5,5" />
        <polygon points="75,5 85,0 75,-5" fill="#8B5CF6" />
      </g>
    </g>
    
    <g transform="translate(600, 200)">
      <circle cx="0" cy="0" r="40" fill="white" stroke="#8B5CF6" strokeWidth="2" />
      
      {/* Store Icon */}
      <g transform="translate(0, 0)">
        <rect x="-15" y="-15" width="30" height="30" rx="2" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M-15,-15 L-10,-25 L10,-25 L15,-15" fill="none" stroke="#8B5CF6" strokeWidth="2" />
        <line x1="-8" y1="-5" x2="8" y2="-5" stroke="#8B5CF6" strokeWidth="1.5" />
        <line x1="-8" y1="5" x2="8" y2="5" stroke="#8B5CF6" strokeWidth="1.5" />
        <line x1="-8" y1="15" x2="8" y2="15" stroke="#8B5CF6" strokeWidth="1.5" />
      </g>
      
      {/* Text label OUTSIDE the node */}
      <text x="0" y="55" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="12" fontWeight="medium">Retailer</text>
      
      {/* Bidirectional arrows */}
      <g>
        <line x1="-40" y1="0" x2="-80" y2="0" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="5,5" />
        <polygon points="-75,5 -85,0 -75,-5" fill="#8B5CF6" />
      </g>
    </g>
    
    {/* Key metrics */}
    <g transform="translate(400, 395)">
      <text x="-120" y="0" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="14" fontWeight="bold">+28% ROI</text>
      <text x="0" y="0" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="14" fontWeight="bold">500+ Stores</text>
      <text x="120" y="0" textAnchor="middle" fill="#333" fontFamily="system-ui" fontSize="14" fontWeight="bold">95% Match Rate</text>
    </g>
  </svg>
);

const ConversionSection = ({ showDeadstock = false, initialData }) => {
  const featuresData = {
    default: [
      {
        id: 1,
        label: "Complete Digital Transformation",
        title: "Your Business, Simplified",
        description: "Transform WhatsApp from a chat app into your entire business ecosystem. Create catalogs, process orders, collect payments, and provide support—all in one familiar interface that requires zero training for both businesses and customers.",
        icon: (
          <svg className="w-12 h-12 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 18.5H9.5C7.85 18.5 6.5 17.15 6.5 15.5V8.5C6.5 6.85 7.85 5.5 9.5 5.5H14.5C16.15 5.5 17.5 6.85 17.5 8.5V12" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 16L13.59 17.59L17.5 13.68" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 9.5H17" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 13H11" />
          </svg>
        ),
        ctaText: "See How It Works"
      },
      {
        id: 2,
        label: "AI Business Agents",
        title: "Your Always-On Business Team",
        description: "Deploy specialized AI agents that run your business 24/7. Sales agents handle inquiries and close deals. Support agents resolve customer issues. Marketing agents send personalized promotions. All automatically, while you focus on growth.",
        icon: (
          <svg className="w-12 h-12 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17.25C9 18.3546 9.44196 19.4144 10.227 20.1995C11.0121 20.9845 12.0718 21.4264 13.1765 21.4264C14.2812 21.4264 15.3408 20.9845 16.1259 20.1995C16.911 19.4144 17.3529 18.3546 17.3529 17.25" />
            <circle cx="13.1765" cy="9.55" r="4.25" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5.98544 16.4118C6.61145 15.4691 7.48104 14.7104 8.5035 14.2149C9.52596 13.7194 10.6602 13.5022 11.7941 13.5882" />
            <circle cx="6.75" cy="8.25" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
          </svg>
        ),
        ctaText: "Meet Your AI Team"
      },
      {
        id: 3,
        label: "80% Cost Reduction",
        title: "Enterprise Power at Fraction of the Cost",
        description: "Launch in days—not months—without expensive development. WymBee delivers the same capabilities as custom apps and websites at 1/5th the cost. Perfect for businesses in emerging markets where every shilling counts.",
        icon: (
          <svg className="w-12 h-12 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6v12m-8-6h16m-10 6h4" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
          </svg>
        ),
        ctaText: "Calculate Your Savings"
      }
    ],
    deadstock: [
      {
        id: 1,
        label: "Smart Inventory Analysis",
        title: "Identify Optimization Opportunities",
        description: "Our AI-powered system analyzes your inventory performance across locations, identifying slow-moving stock before it becomes deadstock. Get actionable recommendations for optimal reallocation to maximize sales potential.",
        icon: (
          <svg className="w-12 h-12 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2h2a2 2 0 012 2v6zm6-10v10m0-10a2 2 0 012-2h2a2 2 0 012 2v6a2 2 0 01-2 2h-2a2 2 0 01-2-2V7z" />
          </svg>
        ),
        ctaText: "See How It Works"
      },
      {
        id: 2,
        label: "Business Community Network",
        title: "Connect with Relevant Retailers",
        description: "Join our network of 500+ vetted retailers and tap into demand across different locations. Our proprietary matching algorithm finds the perfect stores for your excess inventory, ensuring better returns than traditional discounting methods.",
        icon: (
          <svg className="w-12 h-12 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        ctaText: "Explore Network"
      },
      {
        id: 3,
        label: "Inventory Management",
        title: "Free Point of Sale System",
        description: "Get real-time visibility into your inventory with our free POS system. Track stock levels, monitor performance metrics, and make data-driven decisions to prevent future deadstock accumulation. Perfect for stores of all sizes.",
        icon: (
          <svg className="w-12 h-12 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ),
        ctaText: "Try Free POS"
      }
    ]
  };

  const titles = {
    default: {
      label: "The Messaging Revolution",
      title: "Run Your Entire Business Where Customers Already Are",
      description: "Forget expensive apps and websites. WymBee turns WhatsApp and Telegram into complete business platforms—reaching 2+ billion users with zero learning curve."
    },
    deadstock: {
      label: "Smart Solutions",
      title: "Optimize Your Inventory",
      description: "Turn your deadstock problem into an opportunity. Our platform helps you maintain profits without resorting to heavy discounting by connecting you with stores where your products are in demand."
    }
  };

  const activeFeatures = featuresData[showDeadstock ? 'deadstock' : 'default'];
  const activeTitle = titles[showDeadstock ? 'deadstock' : 'default'];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1500px] h-[500px] bg-gradient-to-b from-primary/[0.03] to-transparent" />
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <Container className="relative">
        {/* Header Section */}
        <motion.div 
          className="mx-auto mb-16 max-w-3xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-primary bg-primary/10 rounded-full mb-5">
            {activeTitle.label}
          </span>
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-5 md:text-5xl">
            {activeTitle.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {activeTitle.description}
          </p>
        </motion.div>
        
        {/* Central Illustration - Using the BusinessMessagingHub component */}
        <motion.div
          className="mx-auto mb-20 max-w-4xl"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {showDeadstock ? <DeadstockRedistributionHub /> : <BusinessMessagingHub />}
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {activeFeatures.map((feature, index) => (
            <motion.div
              key={feature.id}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {/* Decorative Corner Shape */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-bl-3xl" />
              
              {/* Icon */}
              <div className="mb-5 relative">
                <div className="p-3 bg-primary/10 inline-block rounded-xl text-primary">
                  {feature.icon}
                </div>
              </div>
              
              {/* Content */}
              <div className="mb-2">
                <span className="text-sm font-medium text-primary">
                  {feature.label}
                </span>
                <h3 className="text-xl font-bold mt-1 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-5">
                  {feature.description}
                </p>
              </div>
              
              {/* CTA */}
              <motion.button 
                className="inline-flex items-center text-primary hover:text-primary-dark transition-colors group"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <span className="font-medium">{feature.ctaText}</span>
                <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" 
                     fill="none" 
                     stroke="currentColor" 
                     viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </motion.button>
            </motion.div>
          ))}
        </div>
        
        {/* Stats Section */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8 mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          {(showDeadstock ? [
            { number: "33%", label: "of SME Inventory Becomes Deadstock Within 3 Months" },
            { number: "KES 233B", label: "Value of Deadstock in Kenya Annually" },
            { number: "28%", label: "Average ROI from Inventory Redistribution" }
          ] : [
            { number: "2B+", label: "WhatsApp Users" },
            { number: "80%", label: "Cost Savings vs Traditional Apps" },
            { number: "3x", label: "Average Revenue Growth" }
          ]).map((stat, index) => (
            <div key={index} className="text-center bg-white rounded-2xl shadow-sm border border-gray-100 py-6 px-4">
              <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </motion.div>
        
        {/* Bottom CTA */}
        <motion.div 
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h3 className="text-2xl font-bold mb-5">Ready to transform your business?</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {showDeadstock ? (
              <motion.a 
                href="https://play.google.com/store/apps/details?id=com.app.wymbee" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-3 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                Download App
              </motion.a>
            ) : (
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/demo"
                  className="inline-block px-6 py-3 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Book a Free Demo
                </Link>
              </motion.div>
            )}
            <motion.a 
              href="https://wymbee.com/account/signup/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors shadow-sm"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              Start Your Free Trial
            </motion.a>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};

export default ConversionSection;